/**
 * The currency module's PURE half: how money is FORMATTED, with no store behind it.
 *
 * 🔴 **It is a separate file for exactly one reason.** `shared.ts` imports core's redux store —
 * that is what `currency()` reads — and importing a module imports everything behind it. A
 * consumer that needs only a formatter or the fallback symbol (`packages/ui/kiosk/theme`, which
 * every kiosk leaf imports) would otherwise drag the whole store graph in, `utils/navigation`'s
 * `createBrowserHistory()` included — a `document is not defined` in any plain-node render. The
 * renderchecks found exactly that.
 *
 * `shared.ts` re-exports all of this, so `@rista/core/utils/currency` remains the one public
 * entry. Import THIS file directly only when you must not pull the store.
 */

/**
 * The app-side fallback symbol. ⚠️ **NOT the symbol table** — that is rista-library's
 * `common.curr`, 118 currencies deep, which `currencySymbolFor` reads first. This has one key.
 *
 * The `string | any` is transcribed verbatim from both apps: it collapses to `any`, which the
 * index-by-unknown-string call sites rely on, so narrowing it would break their types.
 */
export const CURRENCY_CONSTANTS: string | any = {
  INR: "₹",
};

export const INR = "INR";

/* ── merchant defaults: what these answer until the config ships ───────────────────────────── */

/**
 * Digits after the point. TWO because that is the INR convention and every hardcoded
 * `toFixed(2)` in this repo assumes it. `amountDecimals()` in `./shared` is where a merchant's
 * own value will be read.
 */
export const DEFAULT_AMOUNT_DECIMALS = 2;

/** `toFixed` throws `RangeError` outside 0–100. The ceiling is the runtime's, not a product rule. */
export const MAX_AMOUNT_DECIMALS = 100;

/**
 * Grouping: `en-IN` is lakhs and crores (`1,20,00,000`), `en-US` thousands (`12,000,000`).
 * `amountLocale()` in `./shared` is where a merchant's own value will be read.
 *
 * ⚠️ Since `grouping` became a required argument this is no longer a formatter default — it is
 * the value `amountLocale()` answers, and the fallback a host uses when a payload crossing a
 * window boundary did not carry one (`packages/ui/components/CustomerDisplay`).
 *
 * 🔴 **A MERCHANT property, never a call-site option** — which is why nothing takes it as an
 * argument. A screen printing a number must not be the thing deciding whether this business
 * counts in lakhs. ⚠️ A locale moves more than the separators: `fr-FR` makes the DECIMAL mark a
 * comma.
 */
export const DEFAULT_AMOUNT_LOCALE = "en-IN";

/* ── the formatters ────────────────────────────────────────────────────────────────────────── */

/**
 * 🔴 **THE type of a money prop.** Components take one of these instead of a `currencySymbol`, so
 * a raw number can never be pasted beside a symbol — the defect that had `₹4820` sitting next to a
 * bill reading `₹4820.00`. Hosts pass `money` from `./shared`.
 *
 * Named rather than inline: an inline `(value: unknown) => string` silently lacks `options`, which
 * is how a call site discovers it cannot ask for `{ decimals: 0 }`.
 */
export type MoneyFormat = (value: unknown, options?: MoneyOptions) => string;

/**
 * 🔴 **How an amount is RENDERED — the argument the PURE formatters take.** Both fields are
 * REQUIRED, and that is the whole point of the type: nothing here has a default answer, so a
 * caller that omits one is a compile error rather than a surface that quietly inherits a
 * constant.
 *
 * ⚠️ **Not `MoneyOptions`, and the difference is not cosmetic.** That one is the OVERRIDE bag for
 * the store-backed `money()` / `amount()`, where every field has a default answer — so its
 * fields are optional and its `grouping` is a `boolean` meaning *whether*. This one is for code
 * with no store behind it, so its `grouping` is the locale itself (or `false`), because nothing
 * is available to resolve `true` into one.
 *
 * Optional bag for "has defaults"; required spec for "has none".
 */
export interface AmountSpec {
  /** Digits after the point. Validated by `amountDecimalsFor`, so a bad value cannot throw. */
  decimals: number;
  /** The locale to group in — from `amountLocale()`, never a literal — or `false` for none. */
  grouping: string | false;
}

/** What a call site may override for one figure — and deliberately nothing else. */
export interface MoneyOptions {
  /** `0` for a rate base ("per ₹100 spent", a denominator) or an already-whole figure. */
  decimals?: number;
  /**
   * ON by default since 2026-09-19 — every price in the product is grouped. Pass `false` to opt
   * out for one figure. This says WHETHER; `amountLocale()` says HOW.
   */
  grouping?: boolean;
}

/**
 * The precision to use, given whatever the config supplied.
 *
 * 🔴 **A trust boundary, not a tidy-up.** `toFixed` throws outside 0–100, so one bad value off the
 * wire would blank every price on a terminal at once. Nothing reaches `formatAmount` without
 * passing through here.
 *
 * 🔴 **`0` is a legitimate answer** — a whole-unit currency shows no decimals — hence an explicit
 * integer check and never `configured || DEFAULT`, which turns a configured zero into two. Any
 * integer `toFixed` accepts passes: how many digits a merchant wants is their call, and an earlier
 * cap at 4 "because no real currency needs more" overrode a config it was only meant to validate.
 */
export function amountDecimalsFor(configured?: number | null): number {
  const usable =
    typeof configured === "number" &&
    Number.isInteger(configured) &&
    configured >= 0 &&
    configured <= MAX_AMOUNT_DECIMALS;
  return usable ? configured : DEFAULT_AMOUNT_DECIMALS;
}

/**
 * An amount's digits, no symbol: `formatAmount(1234.5, { decimals: 2, grouping: "en-IN" })` -> `"1,234.50"`.
 *
 * `decimals` is REQUIRED — a default would let a call site re-hardcode the thing this module
 * exists to remove.
 *
 * 🔴 **`grouping` is REQUIRED, for the same reason `decimals` is.** Pass a locale string from
 * `amountLocale()`, or `false` to opt out for one figure — but pass something. It carried a
 * `= DEFAULT_AMOUNT_LOCALE` default until 2026-09-19, and that default re-hardcoded `en-IN` at
 * every call site exactly as a `= 2` here would re-hardcode the precision: the three surfaces that
 * format money without going through `money()` (the kiosk's `useKioskMoney`, the customer display
 * and `@rista/payments`' own copy) all rode it, so the day `amountLocale()` answers a merchant's
 * own locale the POS would have followed it and those three would silently have stayed on lakhs.
 *
 * ⚠️ Never a LITERAL locale. The two legitimate arguments are `amountLocale()` and `false`; a
 * host that cannot reach the store (a `packages/ui` leaf) takes it from its provider, the way it
 * already takes `decimals`.
 *
 * ⚠️ Takes `unknown` on purpose: sale documents carry nulls and app prices are sometimes strings,
 * so anything unparseable answers zero at the right precision — absorbing the `|| "0.00"` idiom
 * repeated ~100 times across the two apps.
 *
 * 🔴 **A `NaN` therefore renders as zero, and that silence is a hazard — guard it UPSTREAM.** Every
 * call site already did this (`NaN || 0` is `0`), but a broken calculation reaching a screen as a
 * confident `₹0.00` is worse than an obvious error: `@rista/payments`' `money/calculate.ts` spells
 * out the consequence — the balance reads 0 too, `selectCoversBill` passes, and a sale can be
 * CLOSED having collected nothing. Its `Number.isFinite(Number(x))` at the point the amount is
 * COMPUTED is the right pattern. A formatter cannot tell "genuinely zero" from "arithmetic
 * failed", so it must not try.
 *
 * 🔴 **Its own output is not valid input, and since grouping became the default that is LIVE, not
 * theoretical.** `formatAmount(formatAmount(1200, spec), spec)` is `"0.00"`, because `Number("1,200.00")`
 * is `NaN`. Nothing in the repo does this today (grepped: no `Number(money(…))`, no
 * `value={money(…)}`), and nothing should start — format at the point of DISPLAY, once.
 */
export function formatAmount(value: unknown, format: AmountSpec): string {
  const { decimals, grouping } = format;
  const numeric = Number(value);
  const digits = amountDecimalsFor(decimals);
  // One renderer for the value AND the negative-zero probe below, so the two can never disagree
  // about what "zero at this precision" looks like in this locale.
  const render = (n: number) =>
    grouping
      ? n.toLocaleString(grouping, {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        })
      : n.toFixed(digits);
  const rendered = render(Number.isFinite(numeric) ? numeric : 0);
  // "-0.00" is a lie — a value too small to show is nothing, not minus nothing. Rounding cannot
  // avoid it, since -0.004 becomes a zero that keeps its sign.
  return rendered === `-${render(0)}` ? rendered.slice(1) : rendered;
}

/**
 * Symbol and digits together: `formatMoney(1234.5, "₹", { decimals: 2, grouping: "en-IN" })` -> `"₹1,234.50"`.
 *
 * The everyday form is `money()` in `./shared`, which resolves the arguments itself. This one is
 * for code that must not read a store — the kiosk's leaves, `customerDisplay`, anything pure.
 *
 * `grouping` is REQUIRED here too — see `formatAmount` above for why.
 *
 * 🔴 A NEGATIVE signs the WHOLE thing: `-₹5.50`, never `₹-5.50`. Decided here so no caller has to.
 * Real negatives reach this (`getOrderSummaryRows` emits a negative Discount and Round Off), and
 * they rendered the wrong way round until 2026-09-19. A caller drawing its own `-` passes
 * `Math.abs` — the kiosk's `BillSummarySection` is the one that does.
 */
export function formatMoney(value: unknown, symbol: string, format: AmountSpec): string {
  const digits = formatAmount(value, format);
  // ⚠️ Assumes an ASCII hyphen, which every `en-*` locale uses. A locale signing with U+2212
  // (`sv-SE`) would fall through to `kr−5,50` rather than `−kr5,50`. Unreachable while
  // `amountLocale()` answers `en-IN`; widen it the day the config can return such a locale.
  return digits.startsWith("-") ? `-${symbol}${digits.slice(1)}` : `${symbol}${digits}`;
}
